import { useEffect, useMemo, useState } from "react";
import type { ComponentType } from "react";
import type { HeldOrder, PaymentMethod, Product, Table } from "@ai-pos/shared";
import { categoriesApi, heldOrdersApi, ordersApi, paymentMethodsApi, productsApi, tablesApi } from "../api/resources.js";
import type { Category } from "../api/resources.js";
import { useAuthStore } from "../stores/authStore.js";
import { computeCartTotals, generateId, useCartStore } from "../stores/cartStore.js";
import { useTopbarStore } from "../stores/topbarStore.js";
import { CourseIcon, DessertIcon, DrinkIcon, FoodIcon, ReceiptIcon, ClockIcon, CloseIcon } from "../components/icons.js";
import { AiVoiceConfirmPanel } from "./register/AiVoiceConfirmPanel.js";
import { CheckoutCompletePanel } from "./register/CheckoutCompletePanel.js";
import { CheckoutConfirmPanel } from "./register/CheckoutConfirmPanel.js";
import { TableSelectScreen } from "./register/TableSelectScreen.js";
import type { CheckoutResponse } from "../api/resources.js";

type Step = "cart" | "ai-confirm" | "checkout-confirm" | "complete";

const CATEGORY_ICONS: Record<string, ComponentType<{ size?: number }>> = {
  フード: FoodIcon,
  ドリンク: DrinkIcon,
  スイーツ: DessertIcon,
  コース: CourseIcon,
};

function categoryIcon(name: string) {
  const Icon = CATEGORY_ICONS[name] ?? CourseIcon;
  return <Icon />;
}

export function Register() {
  const [step, setStep] = useState<Step>("cart");
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [heldOrders, setHeldOrders] = useState<HeldOrder[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeSubCategoryId, setActiveSubCategoryId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [holdMessage, setHoldMessage] = useState<string | null>(null);
  const [draftPrinting, setDraftPrinting] = useState(false);
  const [draftPrintMessage, setDraftPrintMessage] = useState<string | null>(null);
  const [checkoutResult, setCheckoutResult] = useState<CheckoutResponse | null>(null);
  const [variablePriceProduct, setVariablePriceProduct] = useState<Product | null>(null);
  const [variablePriceInput, setVariablePriceInput] = useState("");
  const [mobileTab, setMobileTab] = useState<"products" | "cart">("products");

  const staff = useAuthStore((state) => state.staff);
  const cart = useCartStore();
  const setTopbarContent = useTopbarStore((state) => state.setContent);

  const reloadHeldOrders = () => {
    heldOrdersApi
      .list()
      .then((res) => setHeldOrders(res.heldOrders))
      .catch(() => {
        /* テーブル選択画面の在席表示のみに影響するため、ここでは静かに失敗させる */
      });
  };

  useEffect(() => {
    Promise.all([
      productsApi.list(),
      categoriesApi.list(),
      paymentMethodsApi.list(),
      tablesApi.list(),
      heldOrdersApi.list(),
    ])
      .then(([productsRes, categoriesRes, paymentMethodsRes, tablesRes, heldOrdersRes]) => {
        setProducts(productsRes.products.filter((p) => p.isActive && p.isButtonVisible));
        setCategories(categoriesRes.categories.filter((c) => c.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
        setPaymentMethods(paymentMethodsRes.paymentMethods.filter((p) => p.isActive));
        setTables(tablesRes.tables.filter((t) => t.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
        setHeldOrders(heldOrdersRes.heldOrders);
        const topLevel = categoriesRes.categories
          .filter((c) => c.isActive && !c.parentCategoryId)
          .sort((a, b) => a.sortOrder - b.sortOrder);
        if (topLevel.length > 0) {
          setActiveCategoryId(topLevel[0]?.id ?? null);
        }
        if (paymentMethodsRes.paymentMethods.length > 0 && !cart.paymentMethodId) {
          cart.setPaymentMethodId(paymentMethodsRes.paymentMethods[0]?.id ?? null);
        }
      })
      .catch((error: unknown) => {
        setLoadError(error instanceof Error ? error.message : "商品情報の取得に失敗しました");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topLevelCategories = useMemo(() => categories.filter((c) => !c.parentCategoryId), [categories]);
  const subCategories = useMemo(
    () => categories.filter((c) => c.parentCategoryId === activeCategoryId),
    [categories, activeCategoryId],
  );

  const visibleProducts = useMemo(() => {
    if (activeSubCategoryId) {
      return products.filter((p) => p.categoryId === activeSubCategoryId);
    }
    if (!activeCategoryId) return products;
    const matchingIds = new Set([activeCategoryId, ...subCategories.map((c) => c.id)]);
    return products.filter((p) => matchingIds.has(p.categoryId));
  }, [products, activeCategoryId, activeSubCategoryId, subCategories]);

  const totals = computeCartTotals(cart.items, cart.discount);

  const heldOrderByTableId = useMemo(() => {
    const map = new Map<string, HeldOrder>();
    for (const heldOrder of heldOrders) {
      if (heldOrder.tableId) map.set(heldOrder.tableId, heldOrder);
    }
    return map;
  }, [heldOrders]);

  const computeHeldOrderAmount = (heldOrder: HeldOrder): number =>
    heldOrder.items.reduce((sum, item) => {
      const unitPrice = item.unitPrice ?? products.find((p) => p.id === item.productId)?.priceIncludingTax ?? 0;
      return sum + unitPrice * item.quantity;
    }, 0);

  const handleHold = async () => {
    if (cart.items.length === 0 || !cart.tableId) return;
    try {
      await heldOrdersApi.create({
        guestCount: cart.guestCount,
        items: cart.items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          note: item.note,
          unitPrice: item.unitPrice,
        })),
        tableId: cart.tableId,
        note: cart.note,
      });
      cart.clear();
      reloadHeldOrders();
      setHoldMessage("会計を保留にしました。テーブル選択画面から再開できます。");
      setTimeout(() => setHoldMessage(null), 4000);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "保留に失敗しました");
    }
  };

  const handleDraftPrint = async () => {
    if (cart.items.length === 0 || cart.items.some((item) => !item.paymentMethodId)) return;
    setDraftPrinting(true);
    setDraftPrintMessage(null);
    try {
      const res = await ordersApi.draftPrint({
        items: cart.items.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          note: item.note,
          paymentMethodId: item.paymentMethodId as string,
          unitPrice: item.unitPrice,
        })),
        discount: cart.discount,
        guestCount: cart.guestCount,
      });
      setDraftPrintMessage(`【仮伝票】${res.printResult.message}`);
    } catch (error) {
      setDraftPrintMessage(error instanceof Error ? error.message : "仮伝票の印刷に失敗しました");
    } finally {
      setDraftPrinting(false);
    }
  };

  const handleProductClick = (product: Product) => {
    if (product.isVariablePrice) {
      setVariablePriceInput("");
      setVariablePriceProduct(product);
      return;
    }
    cart.addItem({
      id: product.id,
      name: product.name,
      priceIncludingTax: product.priceIncludingTax,
      taxRatePercent: product.taxRatePercent,
    });
  };

  const handleConfirmVariablePrice = () => {
    if (!variablePriceProduct || variablePriceInput.trim() === "") return;
    const amount = Number(variablePriceInput);
    if (!Number.isInteger(amount) || amount < 0) return;
    cart.addItem({
      id: variablePriceProduct.id,
      name: variablePriceProduct.name,
      priceIncludingTax: amount,
      taxRatePercent: variablePriceProduct.taxRatePercent,
    });
    setVariablePriceProduct(null);
    setVariablePriceInput("");
  };

  const handleStartTable = (table: Table) => {
    cart.clear();
    cart.setTableId(table.id);
    cart.setTableName(table.name);
    if (paymentMethods.length > 0) {
      cart.setPaymentMethodId(paymentMethods[0]?.id ?? null);
    }
    setMobileTab("products");
  };

  const handleChangeTable = () => {
    if (cart.items.length > 0) {
      if (!window.confirm("現在のカート内容は破棄されます。よろしいですか？")) return;
    }
    cart.clear();
  };

  const handleResumeTable = async (heldOrder: HeldOrder) => {
    const items = heldOrder.items
      .map((item) => {
        const product = products.find((p) => p.id === item.productId);
        if (!product) return null;
        return {
          rowId: generateId(),
          productId: product.id,
          productName: product.name,
          unitPrice: item.unitPrice ?? product.priceIncludingTax,
          taxRatePercent: product.taxRatePercent,
          quantity: item.quantity,
          paymentMethodId: cart.paymentMethodId,
          note: item.note,
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
    cart.setItems(items);
    cart.setGuestCount(heldOrder.guestCount);
    cart.setTableId(heldOrder.tableId ?? null);
    cart.setTableName(heldOrder.tableName ?? "");
    cart.setNote(heldOrder.note ?? "");
    await heldOrdersApi.remove(heldOrder.id);
    reloadHeldOrders();
    setMobileTab("products");
  };

  useEffect(() => {
    if (cart.tableId) {
      setTopbarContent(
        <button className="table-chip" onClick={handleChangeTable}>
          {cart.tableName} <span className="change">変更</span>
        </button>,
      );
    } else {
      setTopbarContent(null);
    }
    return () => setTopbarContent(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.tableId, cart.tableName]);

  if (step === "ai-confirm") {
    return (
      <AiVoiceConfirmPanel
        products={products}
        paymentMethods={paymentMethods}
        onCancel={() => setStep("cart")}
        onApply={() => setStep("cart")}
      />
    );
  }

  if (step === "checkout-confirm") {
    return (
      <CheckoutConfirmPanel
        paymentMethods={paymentMethods}
        onBack={() => setStep("cart")}
        onCompleted={(result) => {
          setCheckoutResult(result);
          reloadHeldOrders();
          setStep("complete");
        }}
      />
    );
  }

  if (step === "complete" && checkoutResult) {
    return (
      <CheckoutCompletePanel
        result={checkoutResult}
        onNewOrder={() => {
          cart.clear();
          setCheckoutResult(null);
          setStep("cart");
        }}
      />
    );
  }

  if (!cart.tableId) {
    return (
      <>
        {loadError && <div className="alert alert-error">{loadError}</div>}
        {holdMessage && <div className="alert alert-success">{holdMessage}</div>}
        <TableSelectScreen
          tables={tables}
          heldOrderByTableId={heldOrderByTableId}
          computeHeldOrderAmount={computeHeldOrderAmount}
          onStart={handleStartTable}
          onResume={handleResumeTable}
        />
      </>
    );
  }

  const checkoutDisabled = cart.items.length === 0 || cart.items.some((item) => !item.paymentMethodId);

  return (
    <div className="register-layout" data-mobile-tab={mobileTab}>
      <div className="mobile-tabs">
        <button
          type="button"
          className={mobileTab === "products" ? "mobile-tab active" : "mobile-tab"}
          onClick={() => setMobileTab("products")}
        >
          商品
        </button>
        <button
          type="button"
          className={mobileTab === "cart" ? "mobile-tab active" : "mobile-tab"}
          onClick={() => setMobileTab("cart")}
        >
          カート
          {cart.items.length > 0 && <span className="mobile-tab__badge num">{cart.items.length}</span>}
        </button>
      </div>

      <div className="register-products">
        {loadError && <div className="alert alert-error">{loadError}</div>}
        <div className="category-tabs">
          {topLevelCategories.map((category) => (
            <button
              key={category.id}
              className={activeCategoryId === category.id ? "cat-tab active" : "cat-tab"}
              onClick={() => {
                setActiveCategoryId(category.id);
                setActiveSubCategoryId(null);
              }}
            >
              {categoryIcon(category.name)}
              {category.name}
            </button>
          ))}
        </div>
        {subCategories.length > 0 && (
          <div className="category-tabs sub-category-tabs">
            <button
              className={activeSubCategoryId === null ? "cat-tab active" : "cat-tab"}
              onClick={() => setActiveSubCategoryId(null)}
            >
              すべて
            </button>
            {subCategories.map((sub) => (
              <button
                key={sub.id}
                className={activeSubCategoryId === sub.id ? "cat-tab active" : "cat-tab"}
                onClick={() => setActiveSubCategoryId(sub.id)}
              >
                {sub.name}
              </button>
            ))}
          </div>
        )}
        <div className="product-grid">
          {visibleProducts.map((product) => (
            <button key={product.id} className="product-button" onClick={() => handleProductClick(product)}>
              <span className="product-button__name">{product.name}</span>
              {product.isVariablePrice ? (
                <span className="product-button__price product-button__price--variable">都度入力</span>
              ) : (
                <span className="product-button__price num">¥{product.priceExcludingTax.toLocaleString("ja-JP")}</span>
              )}
            </button>
          ))}
          {visibleProducts.length === 0 && <div className="empty-state">商品がありません</div>}
        </div>
      </div>

      <div className="order-rail">
        <div className="rail-head">
          <div className="rail-head-row">
            <span className="rail-title">{cart.tableName} の会計</span>
            <button className="link-btn danger" onClick={() => cart.clearItems()} disabled={cart.items.length === 0}>
              全削除
            </button>
          </div>
          <div className="guest-stepper">
            会計人数
            <span className="stepper">
              <button onClick={() => cart.setGuestCount(cart.guestCount - 1)} aria-label="会計人数を減らす">
                −
              </button>
              <span className="val num">{cart.guestCount}</span>
              <button onClick={() => cart.setGuestCount(cart.guestCount + 1)} aria-label="会計人数を増やす">
                ＋
              </button>
            </span>
          </div>
        </div>

        <div className="cart-items">
          {cart.items.length === 0 && <div className="empty-state">商品を選択してください</div>}
          {cart.items.map((item) => (
            <div key={item.rowId} className="cart-item">
              <div className="cart-item__row1">
                <span className="cart-item__name">
                  {item.productName}
                  {paymentMethods.length <= 1 && (
                    <small>{paymentMethods.find((m) => m.id === item.paymentMethodId)?.name}</small>
                  )}
                </span>
                <span className="cart-item__price num">¥{(item.unitPrice * item.quantity).toLocaleString("ja-JP")}</span>
              </div>
              <div className="cart-item__row2">
                <span className="stepper">
                  <button onClick={() => cart.decrementItem(item.rowId)} aria-label="数量を減らす">
                    −
                  </button>
                  <span className="val num">{item.quantity}</span>
                  <button onClick={() => cart.incrementItem(item.rowId)} aria-label="数量を増やす">
                    ＋
                  </button>
                </span>
                {paymentMethods.length > 1 && (
                  <select
                    className="cart-item__payment"
                    value={item.paymentMethodId ?? ""}
                    onChange={(e) => cart.setItemPaymentMethodId(item.rowId, e.target.value)}
                    aria-label={`${item.productName}の支払方法`}
                  >
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                )}
                <button className="icon-x" onClick={() => cart.removeItem(item.rowId)} aria-label="削除">
                  <CloseIcon size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="rail-footer">
          {paymentMethods.length > 1 && (
            <div className="field">
              <label>支払方法（一括設定）</label>
              <div className="chip-row">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    className={cart.paymentMethodId === method.id ? "chip active" : "chip"}
                    onClick={() => cart.setPaymentMethodId(method.id)}
                  >
                    {method.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="field-row">
            <div className="field">
              <label>値引き（円）</label>
              <span className="stepper" style={{ width: "100%", justifyContent: "space-between" }}>
                <button onClick={() => cart.setDiscount(cart.discount - 100)} aria-label="値引きを減らす">
                  −
                </button>
                <span className="val num">¥{cart.discount.toLocaleString("ja-JP")}</span>
                <button onClick={() => cart.setDiscount(cart.discount + 100)} aria-label="値引きを増やす">
                  ＋
                </button>
              </span>
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="note">メモ</label>
            <input id="note" type="text" value={cart.note} onChange={(e) => cart.setNote(e.target.value)} />
          </div>
          <div className="row" style={{ fontSize: 12.5, color: "var(--color-text-muted)" }}>
            担当: {staff?.name}
          </div>

          <div className="cart-summary">
            <div className="cart-summary__row">
              <span>小計</span>
              <span className="num">¥{totals.subtotal.toLocaleString("ja-JP")}</span>
            </div>
            <div className="cart-summary__row">
              <span>値引き</span>
              <span className="num">¥{cart.discount.toLocaleString("ja-JP")}</span>
            </div>
            <div className="cart-summary__total">
              <span>合計</span>
              <span className="num">¥{totals.total.toLocaleString("ja-JP")}</span>
            </div>
          </div>

          {draftPrintMessage && <div className="alert alert-warning">{draftPrintMessage}</div>}

          <button className="btn" onClick={() => setStep("ai-confirm")}>
            音声・テキスト入力
          </button>
          <div className="secondary-actions">
            <button
              onClick={() => void handleDraftPrint()}
              disabled={cart.items.length === 0 || cart.items.some((item) => !item.paymentMethodId) || draftPrinting}
            >
              <ReceiptIcon size={14} />
              {draftPrinting ? "印刷中..." : "仮伝票"}
            </button>
            <button onClick={() => void handleHold()} disabled={cart.items.length === 0}>
              <ClockIcon size={14} />
              保留
            </button>
          </div>

          <button className="cta-checkout" disabled={checkoutDisabled} onClick={() => setStep("checkout-confirm")}>
            会計確認へ ¥{totals.total.toLocaleString("ja-JP")}
          </button>
        </div>
      </div>

      {variablePriceProduct && (
        <div className="modal-backdrop" onClick={() => setVariablePriceProduct(null)}>
          <div className="modal stack" onClick={(e) => e.stopPropagation()}>
            <h3>{variablePriceProduct.name}</h3>
            <div className="form-field">
              <label htmlFor="variable-price-amount">金額（税込・円）</label>
              <input
                id="variable-price-amount"
                type="number"
                min={0}
                autoFocus
                value={variablePriceInput}
                onChange={(e) => setVariablePriceInput(e.target.value)}
              />
            </div>
            <div className="row">
              <button className="btn" onClick={() => setVariablePriceProduct(null)}>
                キャンセル
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmVariablePrice}
                disabled={
                  variablePriceInput.trim() === "" ||
                  !Number.isInteger(Number(variablePriceInput)) ||
                  Number(variablePriceInput) < 0
                }
              >
                カートに追加
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
